import {
  CaretDown,
  CaretRight,
  Launch,
  CollapseAll,
  ExpandAll,
} from '@carbon/react/icons';
import { Button, Checkbox, Tag } from '@carbon/react';
import classNames from 'classnames';
import { FloatingTooltip } from 'components/floating-tooltip/floating-tooltip';
import React, { useEffect, useState } from 'react';
import StructureGridItem from 'components/structure-grid-item/structure-grid-item';
import { ValidityStatusIndicator } from 'components/structure-grids/validity-status-indicator';
import styles from './structure-grid-collections-list.module.scss';
import { sortCollections } from './utils';
import { Grid, StructureGridCollection } from 'types/api-responses';
import { format0 } from 'utils/number';

interface StructureGridCollectionsProps {
  collections: StructureGridCollection[];
  grids?: Grid[];
  onCheckedChange: (checkedIds: number[]) => void;
  selectedGridIds?: number[];
}

const StructureGridCollectionsList: React.FC<StructureGridCollectionsProps> = ({
  collections,
  grids = [],
  onCheckedChange,
  selectedGridIds,
}) => {
  // Use composite keys: "collectionId-gridId" for collections and "grid-gridId" for individual grids
  const [checked, setChecked] = useState<{ [key: string]: boolean }>({});

  // Helper function to create composite key for collections
  const getCollectionCompositeKey = (
    collectionId: number,
    gridId: number
  ): string => {
    return `collection-${collectionId}-${gridId}`;
  };

  // Helper function to create composite key for individual grids
  const getIndividualGridCompositeKey = (gridId: number): string => {
    return `grid-${gridId}`;
  };

  // Helper function to get gridId from composite key
  const getGridIdFromKey = (key: string): number => {
    return parseInt(key.split('-').pop() || '0');
  };

  // Helper function to check if a grid should be disabled
  const isGridDisabled = (collectionId: number, gridId: number): boolean => {
    // Check if this grid is selected in another collection
    return collections.some((collection) => {
      if (collection.collectionId === collectionId) return false; // Same collection
      return collection.grids.some((grid) => {
        if (grid.gridId !== gridId) return false; // Different grid ID
        const compositeKey = getCollectionCompositeKey(
          collection.collectionId,
          grid.gridId
        );
        return checked[compositeKey]; // Check if this grid is selected in the other collection
      });
    });
  };

  // Helper function to check if an individual grid should be disabled
  const isIndividualGridDisabled = (gridId: number): boolean => {
    // Check if this grid is selected in any collection
    const isSelectedInCollection = collections.some((collection) => {
      return collection.grids.some((grid) => {
        if (grid.gridId !== gridId) return false; // Different grid ID
        const compositeKey = getCollectionCompositeKey(
          collection.collectionId,
          grid.gridId
        );
        return checked[compositeKey]; // Check if this grid is selected in any collection
      });
    });

    // Also check if any collection is fully selected (this should disable all individual grids)
    const isAnyCollectionFullySelected = collections.some((collection) => {
      const itemIds = collection.grids.map((grid) => grid.gridId);
      return (
        itemIds.length > 0 &&
        itemIds.every((gridId: number) => {
          const compositeKey = getCollectionCompositeKey(
            collection.collectionId,
            gridId
          );
          return checked[compositeKey];
        })
      );
    });

    return isSelectedInCollection || isAnyCollectionFullySelected;
  };

  const handleCollectionGridCheck = (
    collectionId: number,
    gridId: number,
    value: boolean
  ) => {
    setChecked((prev) => {
      const newChecked = { ...prev };

      if (value) {
        // If we're checking a grid, disable the same grid ID in other collections and individual grids
        collections.forEach((collection) => {
          if (collection.collectionId !== collectionId) {
            collection.grids.forEach((grid) => {
              if (grid.gridId === gridId) {
                const compositeKey = getCollectionCompositeKey(
                  collection.collectionId,
                  grid.gridId
                );
                newChecked[compositeKey] = false;
              }
            });
          }
        });

        // Also disable the same grid ID in individual grids
        grids.forEach((grid) => {
          if (grid.gridId === gridId) {
            const compositeKey = getIndividualGridCompositeKey(grid.gridId);
            newChecked[compositeKey] = false;
          }
        });
      }

      // Set the clicked grid's state
      const compositeKey = getCollectionCompositeKey(collectionId, gridId);
      newChecked[compositeKey] = value;

      return newChecked;
    });
  };

  const handleIndividualGridCheck = (gridId: number, value: boolean) => {
    setChecked((prev) => {
      const newChecked = { ...prev };

      if (value) {
        // If we're checking an individual grid, disable the same grid ID in collections
        collections.forEach((collection) => {
          collection.grids.forEach((grid) => {
            if (grid.gridId === gridId) {
              const compositeKey = getCollectionCompositeKey(
                collection.collectionId,
                grid.gridId
              );
              newChecked[compositeKey] = false;
            }
          });
        });
      }

      // Set the clicked grid's state
      const compositeKey = getIndividualGridCompositeKey(gridId);
      newChecked[compositeKey] = value;

      return newChecked;
    });
  };

  // Initialize checked state when selectedGridIds prop changes
  useEffect(() => {
    if (selectedGridIds && selectedGridIds.length > 0) {
      setChecked((prev) => {
        const newChecked = { ...prev };

        // Check grids in collections
        collections.forEach((collection) => {
          collection.grids.forEach((grid) => {
            if (selectedGridIds.includes(grid.gridId)) {
              const compositeKey = getCollectionCompositeKey(
                collection.collectionId,
                grid.gridId
              );
              newChecked[compositeKey] = true;
            }
          });
        });

        // Check individual grids
        grids.forEach((grid) => {
          if (selectedGridIds.includes(grid.gridId)) {
            const compositeKey = getIndividualGridCompositeKey(grid.gridId);
            newChecked[compositeKey] = true;
          }
        });

        return newChecked;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Notify parent when checked state changes
  useEffect(() => {
    const checkedIds = Object.entries(checked)
      .filter(([, isChecked]) => isChecked)
      .map(([key]) => getGridIdFromKey(key))
      .filter((gridId) => {
        // Filter out grids that don't intersect
        // Check if it's a collection grid
        const collectionGrid = collections
          .flatMap((col) => col.grids)
          .find((grid) => grid.gridId === gridId);

        if (collectionGrid) {
          return collectionGrid.intersects;
        }

        // Check if it's an individual grid
        const individualGrid = grids.find((grid) => grid.gridId === gridId);
        if (individualGrid) {
          return individualGrid.intersects;
        }

        // If we can't find the grid, don't include it
        return false;
      });
    onCheckedChange(checkedIds);
  }, [checked, onCheckedChange, collections, grids]);

  const [openIndexes, setOpenIndexes] = useState<{ [id: number]: boolean }>({});

  // Find the fully selected collection (if any)
  const fullySelectedCollectionId = collections.find((collection) => {
    const itemIds = collection.grids.map((grid) => grid.gridId);
    return (
      itemIds.length > 0 &&
      itemIds.every((gridId: number) => {
        const compositeKey = getCollectionCompositeKey(
          collection.collectionId,
          gridId
        );
        return checked[compositeKey];
      })
    );
  })?.collectionId;

  // Handler for toggling open/close, but disables if not allowed
  const handleToggle = (id: number, disabled: boolean) => {
    if (disabled) return;
    setOpenIndexes((prev) => ({ ...prev, [id]: !prev[id] }));
  };

  // When a collection becomes fully selected, uncheck all items in other collections and individual grids
  useEffect(() => {
    if (!fullySelectedCollectionId) return;

    setChecked((prev) => {
      const newChecked = { ...prev };

      // Uncheck all items in other collections
      collections.forEach((col) => {
        if (col.collectionId !== fullySelectedCollectionId) {
          col.grids.forEach((grid) => {
            const compositeKey = getCollectionCompositeKey(
              col.collectionId,
              grid.gridId
            );
            newChecked[compositeKey] = false;
          });
        }
      });

      // Uncheck all individual grids when a collection is fully selected
      grids.forEach((grid) => {
        const compositeKey = getIndividualGridCompositeKey(grid.gridId);
        newChecked[compositeKey] = false;
      });

      return newChecked;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fullySelectedCollectionId]);

  return (
    <div>
      {collections.length === 0 && grids.length === 0 ? (
        <div className={styles.emptyMessage}>No Structure Grids Available</div>
      ) : (
        <>
          {/* Collections Section */}
          {collections.length > 0 && (
            <div className={styles.collectionsSection}>
              <div className={styles.collectionActions}>
                <span className={styles.collectionTitle}>Collections</span>
                <span>
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={CollapseAll}
                    iconDescription="Collapse all"
                    className={styles.collapseAllButton}
                    onClick={() => {
                      setOpenIndexes({});
                    }}
                  />
                  <Button
                    kind="ghost"
                    size="sm"
                    hasIconOnly
                    renderIcon={ExpandAll}
                    iconDescription="Expand all"
                    className={styles.expandAllButton}
                    onClick={() => {
                      const allOpen: { [id: number]: boolean } = {};
                      collections.forEach((col) => {
                        allOpen[col.collectionId] = true;
                      });
                      setOpenIndexes(allOpen);
                    }}
                  />
                </span>
              </div>
              {sortCollections(collections).map((collection) => {
                const isOpen = !!openIndexes[collection.collectionId];
                const itemIds = collection.grids.map((grid) => grid.gridId);
                // Only count intersecting grids for the checked count
                const intersectingGridIds = collection.grids
                  .filter((grid) => grid.intersects)
                  .map((grid) => grid.gridId);
                const checkedCount = intersectingGridIds.filter(
                  (gridId: number) => {
                    const compositeKey = getCollectionCompositeKey(
                      collection.collectionId,
                      gridId
                    );
                    return checked[compositeKey];
                  }
                ).length;
                const allChecked =
                  checkedCount === intersectingGridIds.length &&
                  intersectingGridIds.length > 0;
                const noneChecked = checkedCount === 0;
                const someChecked = !allChecked && !noneChecked;
                // Disable if another collection is fully selected and this is not it
                const isDisabled =
                  fullySelectedCollectionId &&
                  fullySelectedCollectionId !== collection.collectionId;
                return (
                  <div
                    key={collection.collectionId}
                    className={classNames(styles.collectionItem, {
                      [styles.disabled]: isDisabled,
                    })}
                  >
                    <div className={styles.headerRow} role="presentation">
                      <span className={styles.headerLeft}>
                        <span
                          className={styles.caret}
                          role="button"
                          aria-disabled={isDisabled ? 'true' : undefined}
                          onClick={() =>
                            handleToggle(collection.collectionId, !!isDisabled)
                          }
                        >
                          {isOpen && !isDisabled ? (
                            <CaretDown />
                          ) : (
                            <CaretRight />
                          )}
                        </span>
                        <span
                          className={classNames(styles.collectionName, {
                            [styles.collectionNameDisabled]: isDisabled,
                          })}
                        >
                          {collection.collectionName}
                        </span>
                        {collection.isDefault && (
                          <FloatingTooltip
                            content="This Structure Grid Collection is the default reference for this area"
                            placement="top"
                          >
                            <Tag size="sm" className={styles.defaultTag}>
                              DEFAULT
                            </Tag>
                          </FloatingTooltip>
                        )}
                        <ValidityStatusIndicator status={collection.validity} />
                      </span>
                      <span className={styles.headerRight}>
                        <Button
                          kind="ghost"
                          size="sm"
                          hasIconOnly
                          renderIcon={Launch}
                          iconDescription={`Open in new tab`}
                          disabled={!!isDisabled}
                          onClick={() => {
                            window.open(
                              `/structure_grid_collections/${collection.collectionId}`,
                              '_blank',
                              'noopener,noreferrer'
                            );
                          }}
                          className={styles.newTabButton}
                        />
                        <span
                          className={classNames(styles.count, {
                            [styles.countDisabled]: isDisabled,
                          })}
                        >
                          {format0(checkedCount)}/
                          {format0(intersectingGridIds.length)}
                        </span>

                        <Checkbox
                          id={`collection-checkbox-${collection.collectionId}`}
                          data-testid={`collection-checkbox-${collection.collectionId}`}
                          checked={!!allChecked}
                          indeterminate={!!someChecked}
                          disabled={!!isDisabled}
                          onChange={(
                            _evt,
                            { checked }: { checked: boolean }
                          ) => {
                            setChecked((prev) => {
                              const newChecked = { ...prev };
                              // If another collection is fully selected, clear all others
                              if (
                                checked &&
                                fullySelectedCollectionId &&
                                fullySelectedCollectionId !==
                                  collection.collectionId
                              ) {
                                collections.forEach((col) => {
                                  if (
                                    col.collectionId !== collection.collectionId
                                  ) {
                                    col.grids.forEach((grid) => {
                                      const compositeKey =
                                        getCollectionCompositeKey(
                                          col.collectionId,
                                          grid.gridId
                                        );
                                      newChecked[compositeKey] = false;
                                    });
                                  }
                                });

                                // Also clear individual grids
                                grids.forEach((grid) => {
                                  const compositeKey =
                                    getIndividualGridCompositeKey(grid.gridId);
                                  newChecked[compositeKey] = false;
                                });
                              }
                              itemIds.forEach((gridId: number) => {
                                // Only check grids that intersect
                                const grid = collection.grids.find(
                                  (g) => g.gridId === gridId
                                );
                                if (grid && grid.intersects) {
                                  const compositeKey =
                                    getCollectionCompositeKey(
                                      collection.collectionId,
                                      gridId
                                    );
                                  newChecked[compositeKey] = checked;
                                }
                              });

                              return newChecked;
                            });
                          }}
                          className={styles.collectionCheckbox}
                          tabIndex={-1}
                          labelText=""
                        />
                      </span>
                    </div>
                    {isOpen && !isDisabled && (
                      <div className={styles.collectionContent}>
                        {collection.grids.map((grid) => (
                          <StructureGridItem
                            key={`${collection.collectionId}-${grid.gridId}`}
                            name={grid.gridSurface}
                            geologicInterval={grid.geologicInterval}
                            source={grid.source}
                            color={grid.displayColor}
                            uniqueId={`${collection.collectionId}-${grid.gridId}`}
                            checked={
                              isDisabled
                                ? false
                                : (() => {
                                    const compositeKey =
                                      getCollectionCompositeKey(
                                        collection.collectionId,
                                        grid.gridId
                                      );
                                    const isChecked = !!checked[compositeKey];
                                    return isChecked;
                                  })()
                            }
                            disabled={isGridDisabled(
                              collection.collectionId,
                              grid.gridId
                            )}
                            intersect={grid.intersects}
                            onChange={(
                              _evt,
                              { checked: checkedValue }: { checked: boolean }
                            ) => {
                              handleCollectionGridCheck(
                                collection.collectionId,
                                grid.gridId,
                                checkedValue
                              );
                            }}
                          />
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Individual Grids Section */}
          {grids.length > 0 && (
            <div className={styles.individualGridsSection}>
              <div className={styles.sectionHeader}>
                <span className={styles.sectionTitle}>
                  Individual Structure Grid Surfaces
                </span>
              </div>
              {grids.map((grid) => {
                const compositeKey = getIndividualGridCompositeKey(grid.gridId);
                const isChecked = !!checked[compositeKey];
                const isDisabled = isIndividualGridDisabled(grid.gridId);

                return (
                  <StructureGridItem
                    key={`individual-${grid.gridId}`}
                    name={grid.gridSurface}
                    geologicInterval={grid.geologicInterval}
                    source={grid.source}
                    color={grid.displayColor}
                    uniqueId={`individual-${grid.gridId}`}
                    checked={isChecked}
                    disabled={isDisabled}
                    intersect={grid.intersects}
                    onChange={(
                      _evt,
                      { checked: checkedValue }: { checked: boolean }
                    ) => {
                      handleIndividualGridCheck(grid.gridId, checkedValue);
                    }}
                  />
                );
              })}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default StructureGridCollectionsList;
